<template lang="pug">
.resident-transfer-modal
  b-modal(v-model="_value" size="lg" @ok="ok" @cancel="cancel" no-close-on-backdrop no-close-on-esc)
    div(slot="modal-title")
      span 施設関係者グループのメンバー情報を他施設へ移動もしくはコピー
    
    .d-flex.flex-column(style="position: relative;")
      .d-flex.flex-column.mb-2
        span.font-strong-color 移動／コピー先施設は事前設定が必要です。ご希望の場合はお問い合わせよりご連絡ください。
        span 施設関係者グループのメンバー情報を他施設へ移動／コピーします。
        span 移動の場合は移動先／コピー先施設を選択後、「本施設の施設関係者グループのメンバー情報を削除する」をチェックしてください。
        span チェックしない場合はコピーとなり、本施設にも施設関係者グループのメンバー情報が残ります。

      .register-body.d-flex.flex-column.mb-2
        span(style="font-weight: bold;") 施設を選択してください
        .mb-2
        .resident-input
          FormInputSelect(v-model="targetFacilityId" name="facilityId" key-name="移動／コピー先施設" :options="facilityOptions"
            :validation-rules="{ required: true }" :styles="{ 'max-width': '540px' }" @change="changeTargetFacilityId" required)

      .register-body.d-flex.flex-column.mb-2(v-if="targetFacilityId")
        span(style="font-weight: bold;") 移動／コピー先施設の施設関係者情報を入力してください。
        .mb-2
        .resident-input
          FormInputSelect(v-model="groupId" name="group-id" key-name="施設関係者グループ" :options="otherUserGroupOptions"
            :validation-rules="{ required: true }" :styles="{ 'max-width': '540px' }" required)

      .d-flex.flex-column(v-if="targetFacilityId")
        span(style="font-weight: bold;") 本施設の施設関係者グループのメンバー情報の削除有無
        .mb-2
        b-form-checkbox(v-model="needDelete" :value="true" :unchecked-value="false") 本施設の施設関係者グループのメンバー情報を削除する

      .error-messages.mt-2(v-if="errorMessage")
          .error-message {{ errorMessage }}

    RelativeLoading(:isBusy="isBusy" :message="loadingMessage")

    .testmail-modal-footer(slot="modal-footer" slot-scope="{ ok, cancel }" style="width:100%;")
      ValidationAlert(v-model="showValidationAlert")
      .buttons.d-flex.justify-content-end
        button.btn.modal-ok-btn(type="button" @click="ok()") OK
        button.btn.modal-cancel-btn(type="button" @click="cancel()") キャンセル
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import CustomValidate from "@/commons/utils/CustomValidate";

import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";
import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import { OtherUserGroupMemberTransferPostModel } from "@/commons/services/OtherUserGroupMembers";

import OtherUserGroupModel from "@/commons/models/OtherUserGroup";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormTimePicker from "@/pc/components/forms/FormTimePicker.vue";

/**
 * ### 施設関係者グループメンバーの移動／コピーモーダル
 */
@Component({
  components: {
    RelativeLoading,
    ValidationAlert,
    FormInput,
    FormInputSelect,
    FormDatetimePicker,
    FormTimePicker
  },
  $_veeValidate: { validator: "new" }
})
export default class TransferMemberInputModal extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;

  /** 表示可否 */
  @Prop({ required: true })
  value!: boolean;

  /** 移動／コピーによるエラーメッセージ */
  @Prop()
  errorMessage!: string;

  /** ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 移動／コピー先の施設 */
  targetFacilityId: number | null = null;
  /** 移動／コピー先の施設選択肢 */
  facilityOptions: Array<{ value: number; label: string }> = [];
  /** 施設関係者グループ */
  groupId: string | null = null;
  /** 施設関係者グループセレクトボックス用項目 */
  otherUserGroupOptions: Array<{ value: number | string; label: string }> = [];
  /** 移動後、現施設から削除するか否か */
  needDelete: boolean = false;

  get _value(): boolean {
    return this.value;
  }

  set _value(newValue: boolean) {
    this.$emit("input", newValue);
    return;
  }

  /**
   * ### vue lifecycle created
   */
  created() {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 部屋の選択肢を作成する
   * - 利用者グループの選択肢を作成する
   * - 入所日の初期値を作成する
   * - 入所送迎時間の初期値を作成する
   * - 退所予定日の初期値を作成する
   * - 退所送迎時間の初期値を作成する
   * - 家族連絡サービスを使用している場合のみ、変更後ステップに「利用者」を追加する
   */
  public async createView(): Promise<void> {
    this.loadingMessage = "読み込み中...";
    this.isBusy = true;

    this.targetFacilityId = null;
    this.needDelete = false;
    const targetFacilities: Array<FacilityGetModel> = await this.getTargetFacilities();
    const facilityOptions: Array<{ value: number; label: string }> = this.createFacilityOptions(targetFacilities);
    this.facilityOptions = facilityOptions;

    this.isBusy = false;

    return;
  }

  public initialize(): void {
    this.groupId = null;
    this.needDelete = false;
    return;
  }

  /**
   * ### 移動先対象施設取得
   */
  public async getTargetFacilities(): Promise<Array<FacilityGetModel>> {
    const targetFacilities: Array<FacilityGetModel> = [];
    for (const targetFacilityId of userStore.facility.otherUserTransferableFacilities) {
      const response: FacilityGetModel = await this.facilitiesService.get(userStore, { targetFacilityId: targetFacilityId });
      targetFacilities.push(response);
    }
    return targetFacilities;
  }

  public createFacilityOptions(targetFacilities: Array<FacilityGetModel>): Array<{ value: number; label: string }> {
    let facilityOptions: Array<{ value: number; label: string }> = [{ value: null as any, label: "" }];
    for (const targetFacility of targetFacilities) {
      facilityOptions.push({
        value: targetFacility.id,
        label: targetFacility.name
      });
    }
    return facilityOptions;
  }

  /**
   * ### 移動先施設の変更イベント
   *
   * - 移動先施設の部屋、利用者グループを取得して選択肢にする
   */
  public async changeTargetFacilityId(): Promise<void> {
    if (!this.targetFacilityId) {
      return;
    }

    this.loadingMessage = "読込み中...";
    this.isBusy = true;

    const otherUserGroups: Array<OtherUserGroupModel> = await this.getOtherUserGroups(this.targetFacilityId);
    const otherUserGroupOptions: Array<{ value: number; label: string }> = this.createOtherUserOptions(otherUserGroups);
    this.otherUserGroupOptions = otherUserGroupOptions;

    this.initialize();
    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループ取得
   */
  public async getOtherUserGroups(facilityId: number): Promise<Array<OtherUserGroupModel>> {
    const response: Array<OtherUserGroupModel> = await this.otherUserGroupsService.gets(userStore, { targetFacilityId: facilityId });
    return response;
  }

  /**
   * ### グループセレクトボックス用項目を作成する
   */
  public createOtherUserOptions(groups: Array<OtherUserGroupModel>): Array<{ value: number; label: string }> {
    const otherUserGroupOptions: Array<{ value: number; label: string }> = [];
    for (const group of groups) {
      otherUserGroupOptions.push({
        value: group.id,
        label: group.name
      });
    }
    return otherUserGroupOptions;
  }

  public async ok(bvModalEvent: any): Promise<void> {
    bvModalEvent.preventDefault();
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.$emit("ok", this.createOtherUserGroupTransferPostModel());
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  public createOtherUserGroupTransferPostModel(): OtherUserGroupMemberTransferPostModel {
    return {
      destinationFacilityId: this.targetFacilityId as number,
      groupId: Number(this.groupId),
      needDelete: this.needDelete
    };
  }

  public cancel(): void {
    this.showValidationAlert = false;
    this.targetFacilityId = null;
    this.needDelete = false;
    this.$emit("cancel");
    return;
  }
}
</script>
